import { controlAnswerKey } from "../../utils/regulatoryIdentity"
import { useCallback } from "react"
import { useCatalogResource } from "../../pages/Regulatory/useCatalog"
import { allCatalogPages, regulatoryApi } from "../../utils/regulatoryApi"
import { Alert, Button, Card, Select, Space, Tag, Typography } from "antd"
import { Link } from "react-router-dom"
import { StorageEntityType } from "../../generated/proto-ts/main"
import {
  useAssignedCatalog,
  useRegulatoryTemplates,
} from "../../pages/Regulatory/useOperationalCatalog"
import { calculateEN1176Stats } from "../../types"
import { RegulatoryScope } from "../../types/regulatory"
import { scopeLabels } from "../../utils/regulatory"
import { useRegulatoryAnswers } from "./useRegulatoryAnswers"
export function TemplateSelector({
  value,
  onChange,
  scope,
  disabled,
}: {
  value: string | null
  onChange: (value: string) => void
  scope?: RegulatoryScope
  disabled?: boolean
}) {
  const resource = useRegulatoryTemplates(scope)
  const items = (resource.data ?? []).filter((t) => scope || t.scope !== "site")
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {resource.error && (
        <Alert
          type="error"
          message={resource.error.message}
          action={<Button onClick={resource.reload}>Réessayer</Button>}
        />
      )}
      <Select
        aria-label="Modèle réglementaire"
        style={{ width: "100%" }}
        loading={resource.loading}
        disabled={disabled || resource.loading || !!resource.error}
        value={value || undefined}
        placeholder="Choisir un modèle actif"
        showSearch
        optionFilterProp="label"
        onChange={onChange}
        options={items.map((t) => ({
          value: t.uuid,
          label: `${t.name} · ${scopeLabels[t.scope]} · v${t.sourceVersion} · ${t.validated ? "Relu" : "Non relu"}`,
        }))}
      />
      {value && !resource.loading && !resource.error && !items.some((t) => t.uuid === value) && (
        <Alert
          type="warning"
          message="Le modèle affecté est indisponible ou incompatible. Sélectionnez un remplacement."
        />
      )}
      {value && (
        <Link to={`/regulatory/templates/${value}`}>Consulter les contrôles et la couverture</Link>
      )}
    </Space>
  )
}
export function CatalogAssignment({
  catalog,
  assigned,
  selected,
  onChange,
  scope,
  disabled,
  selectedRevision,
  onRevisionChange,
}: {
  selectedRevision?: string
  onRevisionChange: (value: string) => void
  catalog: ReturnType<typeof useAssignedCatalog>
  assigned: string
  selected: string | null
  onChange: (value: string) => void
  scope: RegulatoryScope
  disabled?: boolean
}) {
  const revisions = useCatalogResource(
    useCallback(
      (signal) =>
        selected
          ? allCatalogPages((filters, s) => regulatoryApi.revisions(selected, filters, s), signal)
          : Promise.resolve([]),
      [selected],
    ),
  )
  const value = catalog.data?.resolved
  return (
    <Card title="Modèle réglementaire et couverture">
      <Space direction="vertical" style={{ width: "100%" }}>
        <TemplateSelector
          scope={scope === "site" ? "site" : undefined}
          value={selected}
          onChange={onChange}
          disabled={disabled}
        />
        {selected && (
          <>
            <Select
              aria-label="Révision du modèle"
              style={{ width: "100%" }}
              placeholder={
                catalog.data?.questionnaire.templateUuid === selected &&
                !catalog.data?.questionnaire.versioned
                  ? "Version historique inconnue — choisir explicitement une révision"
                  : "Révision courante à la prochaine affectation"
              }
              value={selectedRevision}
              loading={revisions.loading}
              disabled={disabled || revisions.loading || !!revisions.error}
              options={(revisions.data ?? []).map((r) => ({
                value: r.uuid,
                label: `v${r.sourceVersion} · ${r.definition.validated ? "Relu" : "Non relu"} · ${r.uuid}`,
              }))}
              onChange={onRevisionChange}
            />
            {revisions.error && (
              <Alert
                type="error"
                message={revisions.error.message}
                action={<Button onClick={revisions.reload}>Réessayer</Button>}
              />
            )}
          </>
        )}
        {catalog.data?.questionnaire.templateUuid && !catalog.data.questionnaire.versioned && (
          <Alert
            showIcon
            type="warning"
            message="Version historique inconnue"
            description="Les réponses enregistrées restent consultables. Choisissez puis enregistrez explicitement une révision pour adopter une grille ; sa compatibilité sera vérifiée par le serveur."
          />
        )}
        {selectedRevision &&
          selectedRevision !== catalog.data?.questionnaire.templateRevisionUuid && (
            <Alert
              type="info"
              message="Enregistrez la fiche pour adopter cette révision. Le serveur vérifiera sa compatibilité avec les réponses existantes."
            />
          )}
        {!assigned && (
          <Alert
            showIcon
            type="warning"
            message="Aucun modèle affecté"
            description="Sélectionnez puis enregistrez un modèle pour charger le questionnaire."
          />
        )}
        {selected !== (assigned || null) && (
          <Alert
            type="info"
            message="Enregistrez la fiche pour appliquer le changement de modèle. Le serveur vérifiera la compatibilité avec les réponses existantes."
          />
        )}
        {catalog.error && (
          <Alert
            type="error"
            showIcon
            message={catalog.error.message}
            action={<Button onClick={catalog.reload}>Réessayer</Button>}
          />
        )}
        {catalog.loading && assigned && (
          <Typography.Text>Chargement du questionnaire…</Typography.Text>
        )}
        {value && (
          <>
            <Space wrap>
              <Tag>
                {catalog.template?.Name} · v{catalog.data?.detail?.revision.sourceVersion}
              </Tag>
              <Tag>{value.controls.length} contrôles</Tag>
              <Tag color={value.allReviewed ? "green" : "orange"}>
                {value.allReviewed ? "Modules relus" : "Relecture à compléter"}
              </Tag>
            </Space>
            <Typography.Text type="secondary">
              {catalog.data?.detail?.revision.definition.coverage || "Couverture non renseignée"}
            </Typography.Text>
            {(value.missingParts.length > 0 ||
              value.coverageGaps > 0 ||
              value.interpretationReserves > 0) && (
              <Alert
                type="warning"
                showIcon
                message="Limites de couverture"
                description={`${value.coverageGaps} compléments absents ; ${value.interpretationReserves} réserves. ${value.missingParts.join(" ; ")}`}
              />
            )}
            <Typography.Text type="secondary">
              Cette révision est affectée à l’inspection. La publication d’une nouvelle version ne
              la modifie pas.
            </Typography.Text>
          </>
        )}
      </Space>
    </Card>
  )
}

export function EquipmentProgress({ uuid, templateUuid }: { uuid: string; templateUuid: string }) {
  const catalog = useAssignedCatalog("equipment", uuid)
  const resource = useRegulatoryAnswers(uuid, StorageEntityType.EQUIPMENT)

  if (catalog.error || resource.error) return <Tag color="red">État indisponible</Tag>
  if (catalog.loading || resource.loading || !resource.connected) return <Tag>État non chargé</Tag>
  if (!templateUuid && !catalog.data?.contextual?.configuration.enabled)
    return <Tag>Modèle à affecter</Tag>
  if (!catalog.data?.questionnaire.versioned)
    return <Tag color="orange">Version historique inconnue</Tag>
  const stats = calculateEN1176Stats(catalog.points.map(controlAnswerKey), resource.answers)
  return (
    <Space direction="vertical" size={2}>
      <Tag color="cyan">
        {catalog.data?.contextual?.configuration.enabled
          ? `${catalog.data.contextual.configuration.contexts.filter((c) => c.kind === "component").length} composant(s)`
          : catalog.template?.Name ?? "Modèle affecté"}
      </Tag>
      <Tag color={stats.isCompliant ? "green" : stats.nonConformeCount ? "red" : "orange"}>
        {stats.answered === 0
          ? "Non démarré"
          : stats.answered < stats.total
            ? `Incomplet (${stats.answered}/${stats.total})`
            : stats.isCompliant
              ? `Renseigné (${stats.answered}/${stats.total})`
              : `${stats.nonConformeCount} non conforme(s), ${stats.aControlerCount} à contrôler, ${stats.observationCount} observations, ${stats.notVerifiedCount} non vérifiés`}
      </Tag>
    </Space>
  )
}
